import { useCallback } from 'react';
import { button, folder } from 'leva';
import type { Schema } from 'leva/dist/declarations/src/types/public.js';
import type { SplatDevHandles } from '../../state/splatDevTools';

type ChangeContext = { initial?: boolean };

/** Read by `sdkPoiMarkers.css`: a dot on each beacon's 3D anchor. */
const ANCHOR_DOT_ATTRIBUTE = 'data-nav-anchor-dot';

/**
 * "Nav Beacons" block for the SDK dev panel (`overall`) — the port of the
 * legacy `NavPoiTuner`. A slider writes `config.navigationPois` in place, which
 * the nav cinematic reads at click time, and moves the marker with
 * `updatePoi`. Session-only: copy the result into `overall.ts`.
 */
export const useNavBeaconTuner = (
  handles: SplatDevHandles | null,
): { buildSchema: () => Schema; schemaDeps: unknown[] } => {
  const buildSchema = useCallback((): Schema => {
    const navigationPois = handles?.config.navigationPois ?? [];
    if (!handles || navigationPois.length === 0) return {};
    const { engine } = handles;
    const schema: Schema = {
      'Show Anchor': {
        value: false,
        hint: 'Точка самого 3D-якоря под поднятым глифом: `position.y` ставит её на поверхность модели.',
        onChange: (on: boolean) => {
          engine.getContainer().toggleAttribute(ANCHOR_DOT_ATTRIBUTE, on);
        },
      },
    };
    for (const poi of navigationPois) {
      const axis = (key: 'x' | 'y' | 'z', min: number, max: number) => ({
        label: key,
        value: poi.position[key],
        min,
        max,
        step: 0.01,
        onChange: (value: number, _path: string, ctx: ChangeContext) => {
          if (ctx.initial) return;
          poi.position[key] = value;
          const { x, y, z } = poi.position;
          engine.updatePoi(
            { id: `nav:${poi.targetSceneId}`, position: [x, y, z] },
            { mode: 'patch' },
          );
        },
      });
      // Keys are prefixed: Leva's keys are flat across folders.
      schema[poi.targetSceneId] = folder({
        [`${poi.targetSceneId}_x`]: axis('x', -5, 5),
        [`${poi.targetSceneId}_y`]: axis('y', -0.5, 1.5),
        [`${poi.targetSceneId}_z`]: axis('z', -5, 5),
      });
    }
    schema['Copy Positions'] = button(() => {
      const positions = Object.fromEntries(
        navigationPois.map((poi) => [poi.targetSceneId, poi.position]),
      );
      const json = JSON.stringify(positions, null, 2);
      navigator.clipboard?.writeText(json);
      // eslint-disable-next-line no-console -- dev panel output, for when the clipboard is refused
      console.log(`[SplatDevPanel] Nav beacon positions:\n${json}`);
    });
    return schema;
  }, [handles]);

  return { buildSchema, schemaDeps: [buildSchema] };
};
